import { Base } from '../../abstract/base.model';
import { AgremoStatistics } from '../response-models/agremo-statistics.model';
import { isRecord } from '../utils/is-record';
import { nullableNumber, nullableString } from '../utils/wire-value-utils';
import { AgremoAnalysisStatus } from './agremo-analysis-status.model';

/**
 * An Agremo analysis, built from `/admin/agremo/analyses` and `/admin/organizations/:id/agremo/analyses` JSON
 * (hub `admin-agremo-analyses-controller.ts`). Read-only: the admin has no write route for analyses.
 * Fields the hub didn't send stay `undefined`; an explicit `null` stays `null`. The hub strips `callbackToken`.
 */
export class AgremoAnalysis extends Base {
  organizationId?: string | null;
  organizationName?: string | null;
  mapId?: string | null;
  agremoAnalysisId?: string | null;
  analysisTypeId?: string | null;
  analysisTypeName?: string | null;
  status?: AgremoAnalysisStatus | null;
  progress?: number | null;
  /** ISO timestamp as sent by the hub. */
  requestedAt?: string | null;
  /** ISO timestamp as sent by the hub. */
  completedAt?: string | null;
  errorMessage?: string | null;
  creditsConsumed?: number | null;
  fieldId?: string | null;
  fieldName?: string | null;
  analysisType?: string | null;
  /** Opaque Agremo request parameters, shown as-is. */
  analysisParams?: unknown;
  statistics?: AgremoStatistics | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.readIdentity(source);
    this.readProgress(source);
  }

  private readIdentity(source: Record<string, unknown>): void {
    this.organizationId = nullableString(source['organizationId']);
    this.organizationName = nullableString(source['organizationName']);
    this.mapId = nullableString(source['mapId']);
    this.agremoAnalysisId = nullableString(source['agremoAnalysisId']);
    this.analysisTypeId = idOrNullable(source['analysisTypeId']);
    this.analysisTypeName = nullableString(source['analysisTypeName']);
    this.analysisType = nullableString(source['analysisType']);
    this.fieldId = nullableString(source['fieldId']);
    this.fieldName = nullableString(source['fieldName']);
  }

  private readProgress(source: Record<string, unknown>): void {
    this.status = nullableString(source['status']) as AgremoAnalysisStatus | null | undefined;
    this.progress = nullableNumber(source['progress']);
    this.requestedAt = nullableString(source['requestedAt']);
    this.completedAt = nullableString(source['completedAt']);
    this.errorMessage = nullableString(source['errorMessage']);
    this.creditsConsumed = nullableNumber(source['creditsConsumed']);
    this.analysisParams = source['analysisParams'];
    this.statistics = toStatistics(source['statistics']);
  }
}

/** Agremo type ids arrive as strings or numbers; keep them as strings. */
function idOrNullable(value: unknown): string | null | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : nullableString(value);
}

function toStatistics(value: unknown): AgremoStatistics | null | undefined {
  if (value === null) {
    return null;
  }
  return isRecord(value) ? (value as AgremoStatistics) : undefined;
}
